import { useState, useCallback } from 'react';
import { apiService } from '@/services/api';
import type { ApiError } from '@/types';

export interface UseApiState<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
}

export interface UseApiReturn<T> extends UseApiState<T> {
    execute: () => Promise<void>;
    reset: () => void;
    setData: (data: T | null) => void;
}

export const useApi = <T>(
    apiCall: () => Promise<T>,
    initialData: T | null = null
): UseApiReturn<T> => {
    const [state, setState] = useState<UseApiState<T>>({
        data: initialData,
        loading: false,
        error: null,
    });

    const execute = useCallback(async () => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const result = await apiCall();
            setState(prev => ({ ...prev, data: result, loading: false }));
        } catch (error) {
            let errorMessage = 'An unexpected error occurred';

            if (error && typeof error === 'object' && 'message' in error) {
                errorMessage = (error as ApiError).message;
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }

            setState(prev => ({ ...prev, error: errorMessage, loading: false }));
        }
    }, [apiCall]);

    const reset = useCallback(() => {
        setState({
            data: initialData,
            loading: false,
            error: null,
        });
    }, [initialData]);

    const setData = useCallback((data: T | null) => {
        setState(prev => ({ ...prev, data }));
    }, []);

    return {
        ...state,
        execute,
        reset,
        setData,
    };
};

// Specialized hooks for each data type
export const useUserData = () => {
    return useApi(() => apiService.getUser());
};

export const usePenalties = () => {
    return useApi(() => apiService.getPenalties(), []);
};

export const useParkings = () => {
    return useApi(() => apiService.getParkings(), []);
};

export const useCars = () => {
    return useApi(() => apiService.getCars(), []);
};

export const useCreditCards = () => {
    return useApi(() => apiService.getCreditCards(), []);
};
